import type { MessageEntry } from "./message-model.js";

export type T = (k: string, p?: Record<string, string | number>) => string;

/** Short, stable, non-PII sender label (last 8 of the DID). */
export function senderLabel(t: T, author: string, me: string): string {
  return author === me ? t("talk.you") : `@${author.slice(-8)}`;
}

/** Compact relative age ("now" / "5m" / "2h" / "3d") of `iso` vs `nowMs`. Empty for an
 *  unparseable time. Compact units are language-neutral (standard chat-timestamp convention). */
export function agoLabel(iso: string, nowMs: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.round((nowMs - then) / 1000));
  if (sec < 60) return "now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

export function bandLabel(t: T, e: MessageEntry): string {
  if (e.band === "area") return t("talk.band.area");
  if (e.band === "region") return t("talk.band.region", { km: e.km ?? 0 });
  return t("talk.band.distant");
}

/** Build one feed row for a message. Shared by the talk feed controller (and tests). */
export function messageRow(t: T, m: MessageEntry, me: string, nowMs: number): HTMLElement {
  const row = document.createElement("div");
  row.className = `talk-msg${m.author === me ? " talk-msg--mine" : ""}`;
  row.setAttribute("data-msg", m.id);

  const head = document.createElement("div");
  head.className = "talk-msg__head bx-microlabel";
  const ago = agoLabel(m.createdAtIso, nowMs);
  head.textContent = `${senderLabel(t, m.author, me)} · ${bandLabel(t, m)}${ago ? ` · ${ago}` : ""}`;
  row.append(head);

  const body = document.createElement("p");
  body.className = m.text === null ? "talk-msg__body talk-msg__body--locked" : "talk-msg__body bx-body";
  body.textContent = m.text === null ? t("talk.placeholder.locked") : m.text;
  row.append(body);

  return row;
}
